import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import { Label } from "@/components/ui/label"
import { 
  Card,
  CardContent, 
  CardDescription, 
  CardHeader, 
  CardTitle 
} from "@/components/ui/card"
import { useOvertime } from "@/contexts/overtime/useOvertime"
import { OvertimeRequest } from "@/types"
import { formatDate, formatHours } from "@/lib/utils"
import { OvertimeStatus, UserRole } from "@/types/enums"
import { toast } from "sonner"
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert"
import { ShieldAlert, UserX } from "lucide-react"
import { useAuth } from "@/contexts/auth"
import { getStatusColor } from "@/utils/statusUtils"
import { Badge } from "@/components/ui/badge"

// Extended type for requests with joined reason text
interface OvertimeRequestWithJustificationText extends OvertimeRequest {
  justification_text?: string;
}

interface ApprovalFormProps {
  request: OvertimeRequestWithJustificationText
  type: 'approve' | 'reject'
}

export function ApprovalForm({ request, type }: ApprovalFormProps) {
  const navigate = useNavigate()
  const { approveOvertimeRequest, rejectOvertimeRequest, approveOvertimeRequestAsVP } = useOvertime()
  const [comment, setComment] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const { user } = useAuth()

  // Check if the request needs VP approval
  const requiresVPApproval = request.vpApprovalRequired || request.status === OvertimeStatus.PENDING_VP_APPROVAL
  const isSuperAdmin = user?.role === UserRole.SUPER_ADMIN
  const isDepartmentHead = user?.role === UserRole.DEPARTMENT_HEAD
  
  // Check if the current user is the submitter of the request
  const isRequestSubmitter = user?.id === request.submittedById || 
                            user?.id === request.employeeId
  
  // Department heads cannot approve their own requests
  const cannotApproveSelfSubmitted = isDepartmentHead && isRequestSubmitter

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!comment.trim()) {
      toast.error("Please provide a comment before proceeding", { duration: 1500 });
      return;
    }
    
    // Prevent department heads from approving their own requests
    if (cannotApproveSelfSubmitted && type === 'approve') {
      toast.error("You cannot approve requests you've submitted yourself", { duration: 1500 });
      return;
    }
    
    setIsLoading(true)
    
    try {
      let success = null;
      
      if (type === 'approve') {
        // Use the correct approval function based on the request and user role
        if (requiresVPApproval && isSuperAdmin) {
          success = await approveOvertimeRequestAsVP(request.id, comment);
        } else {
          success = await approveOvertimeRequest(request.id, comment);
        }
      } else {
        success = await rejectOvertimeRequest(request.id, comment);
      }
      
      if (success) {
        navigate('/pending-requests');
      }
    } catch {
      toast.error("An error occurred while processing the request", { duration: 1500 });
    } finally {
      setIsLoading(false);
    }
  }

  const title = type === 'approve' ? "Approve Overtime Request" : "Reject Overtime Request"
  const buttonText = type === 'approve' ? "Approve Request" : "Reject Request"
  const buttonClass = type === 'approve' ? "bg-green-600 hover:bg-green-700" : "bg-red-600 hover:bg-red-700"

  return (
    <form onSubmit={handleSubmit}>
      <Card>
        <CardHeader>
          <CardTitle>{title}</CardTitle>
          <CardDescription>
            Review the request details and provide your {type === 'approve' ? 'approval' : 'rejection'} comment
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Show warning for VP approval required */}
          {requiresVPApproval && !isSuperAdmin && type === 'approve' && (
            <Alert variant="warning">
              <ShieldAlert className="h-4 w-4" />
              <AlertTitle>VP Approval Required</AlertTitle>
              <AlertDescription>
                This request exceeds the standard overtime threshold and requires VP approval.
                Only Super Admins can approve this request.
              </AlertDescription>
            </Alert>
          )}
          
          {/* Show warning for self-submitted requests */}
          {cannotApproveSelfSubmitted && type === 'approve' && (
            <Alert variant="destructive">
              <UserX className="h-4 w-4" />
              <AlertTitle>Cannot Approve Own Request</AlertTitle>
              <AlertDescription>
                You cannot approve requests that you have submitted yourself.
                Please have another authorized person review this request.
              </AlertDescription>
            </Alert>
          )}
          
          <div className="p-4 bg-muted rounded-md">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <h3 className="text-sm font-medium text-muted-foreground">Employee</h3>
                <p className="text-base">{request.employeeName}</p>
              </div>
              <div>
                <h3 className="text-sm font-medium text-muted-foreground">Department</h3>
                <p className="text-base">{request.department}</p>
              </div>
              <div>
                <h3 className="text-sm font-medium text-muted-foreground">Date</h3>
                <p className="text-base">{formatDate(request.date)}</p>
              </div>
              <div>
                <h3 className="text-sm font-medium text-muted-foreground">Hours</h3>
                <div className="flex items-center gap-2">
                  <p className="text-base">{formatHours(request.hours)}</p>
                  {requiresVPApproval && (
                    <Badge variant="outline" className={getStatusColor('warning', 'light')}>
                      <ShieldAlert className="h-3 w-3 mr-1" />
                      <span>Exceeds Threshold</span>
                    </Badge>
                  )}
                </div>
              </div>
            </div>
            <div className="mt-3">
              <h3 className="text-sm font-medium text-muted-foreground">Justification</h3>
              <p className="text-base">{request.justification_text || request.justification}</p>
            </div>
          </div>
          
          <div>
            <Label htmlFor="comment">Your Comment</Label>
            <Textarea
              id="comment"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={type === 'approve' ? 
                "Provide any additional details or instructions" : 
                "Please provide a reason for rejecting this request"
              }
              required
              rows={4}
            />
          </div>
          
          <div className="flex justify-between mt-6">
            <Button 
              variant="outline" 
              type="button" 
              onClick={() => navigate('/pending-requests')}
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              className={buttonClass}
              disabled={isLoading || 
                (requiresVPApproval && !isSuperAdmin && type === 'approve') || 
                (cannotApproveSelfSubmitted && type === 'approve')}
            >
              {isLoading ? "Processing..." : buttonText}
            </Button>
          </div>
        </CardContent>
      </Card>
    </form>
  )
}

export default ApprovalForm
